import React from 'react'
import { Chip } from '@mui/material'
import { fmtDay, num } from '../constants'

/** What a PTL merchant has set up, as small chips (from GET /api/ptl/setup/merchants). */
export const statusOf = (m) => ({
  pricing: (m?.rateCardCouriers || []).length > 0,
  allocation: (m?.allocationCouriers || []).length > 0,
  customTat: (m?.customTatLanes || 0) > 0,
  ownMap: (m?.ownZoneMapPincodes || 0) > 0,
})

export const PricingChip = ({ m }) => {
  const n = (m?.rateCardCouriers || []).length
  return <Chip size='small' label={n ? `Rate cards: ${n}` : 'No rate card'} color={n ? 'success' : 'error'} variant='outlined' />
}

export const TatChip = ({ m }) => (
  m?.customTatLanes
    ? <Chip size='small' label={`TAT: custom · ${num(m.customTatLanes)} lanes`} color='warning' />
    : <Chip size='small' label='TAT: standard' variant='outlined' />
)

export const ZoneChip = ({ m }) => (
  m?.ownZoneMapPincodes
    ? <Chip size='small' label={`Zone map: own · ${num(m.ownZoneMapPincodes)} pincodes`} color='warning' />
    : <Chip size='small' label='Zone map: standard' variant='outlined' />
)

export const AllocationChip = ({ m }) => {
  const n = (m?.allocationCouriers || []).length
  return <Chip size='small' label={n ? `Allocation: ${n} courier${n > 1 ? 's' : ''}` : 'No allocation couriers'} color={n ? 'success' : 'error'} variant='outlined' />
}

/**
 * Onboarding state per step, read from the data (no status is stored). TAT is always done: the Prozo standard applies
 * until a custom TAT is uploaded. Allocation is done once there is a rate card: with no couriers picked, pickups go to
 * the cheapest courier with a rate card. It is locked until then, because only couriers with a price for the merchant
 * can be allocated.
 */
export const stepStates = (m) => {
  const s = statusOf(m)
  return {
    pricing: { done: s.pricing },
    tat: { done: true, custom: s.customTat },
    allocation: {
      done: s.pricing || s.allocation,
      custom: s.allocation,
      lockedBy: s.allocation || s.pricing ? null : 'Needs a rate card from Step 1 first: only couriers with a price for this merchant can be allocated.',
    },
  }
}

/** Only a rate card is required: TAT and allocation have defaults (standard TAT, cheapest carded courier). */
export const isFullySetUp = (m) => statusOf(m).pricing

/** Key of the first step that is not done, or null when fully set up. */
export const nextStepKey = (m, steps) => {
  const st = stepStates(m)
  const next = steps.find((x) => !st[x.key].done)
  return next ? next.key : null
}

/** Merchant status (merchants.status) as KYC wording. PTL booking refuses an order unless it is KYC_APPROVED. */
const KYC = {
  KYC_APPROVED: { tone: 'done', label: 'KYC approved' },
  KYC_UPLOADED: { tone: 'pending', label: 'KYC docs uploaded' },
  KYC_PENDING: { tone: 'blocked', label: 'KYC pending' },
}

/**
 * Can the merchant book PTL orders? Read from the data, never stored. Booking needs KYC approved, the pickup warehouse
 * named on the order (so at least one pickup location) and a price (a rate card). KYC and pickup do not block Steps 1-3.
 */
export const readiness = (m) => {
  const kycOk = m?.merchantStatus === 'KYC_APPROVED'
  const pickups = m?.pickupLocations || 0
  const kyc = KYC[m?.merchantStatus] || { tone: 'blocked', label: m?.merchantStatus ? `KYC: ${m.merchantStatus}` : 'KYC not started' }
  const missing = [
    !kycOk && 'KYC approval',
    !pickups && 'a pickup location',
    !statusOf(m).pricing && 'a rate card',
  ].filter(Boolean)
  return {
    kyc: { ok: kycOk, ...kyc },
    pickup: { ok: pickups > 0, tone: pickups ? 'done' : 'blocked', label: pickups ? `${pickups} pickup location${pickups > 1 ? 's' : ''}` : 'No pickup location' },
    ready: missing.length === 0,
    missing,
  }
}

/** Credit days in words: "30 days credit", "Advance (0 days credit)", or '' when not entered. */
export const creditDaysText = (days) => (days == null ? '' : days === 0 ? 'Advance (0 days credit)' : `${days} days credit`)
export const termsText = (a) => creditDaysText(a?.creditDays)

/** What a pending agreement still lacks before it can be approved (empty = ready for an admin to approve). */
export const agreementMissing = (a) => [!a?.signed && 'the signed agreement', a?.creditDays == null && 'the credit days'].filter(Boolean)

/**
 * Agreement (GET merchants → agreement) as chip tone + label. A pending agreement without the signed document or the
 * credit days (dates entered on PTL Billing, or saved before the upload) is "Incomplete": no admin can approve it yet. Expiry first (the warning window is agreementExpiringDays
 * on PTL Billing settings), then the approval. Does not block booking. attention = something to do; expiry = expiring or
 * expired. undefined = still loading (loadAgreementsInto), null = not read (the agreement store did not answer).
 */
export const agreementStatus = (a) => {
  if (a === undefined) return { tone: 'none', label: 'Loading…', attention: false, expiry: false }
  if (!a) return { tone: 'none', label: 'Not read', attention: false, expiry: false }
  const till = fmtDay(a.expiryDate)
  if (a.approvalStatus === 'NOT_SUBMITTED') return { tone: 'blocked', label: 'No agreement', attention: true, expiry: false }
  if (a.validity === 'EXPIRED') return { tone: 'blocked', label: `Expired ${till}`, attention: true, expiry: true }
  if (a.validity === 'EXPIRING') {
    const d = a.daysToExpiry
    return { tone: 'pending', label: d === 0 ? 'Expires today' : `Expires in ${d} day${d === 1 ? '' : 's'}`, attention: true, expiry: true }
  }
  if (a.approvalStatus === 'REJECTED') return { tone: 'blocked', label: 'Rejected', attention: true, expiry: false }
  if (a.approvalStatus === 'PENDING') {
    return agreementMissing(a).length
      ? { tone: 'blocked', label: 'Incomplete', attention: true, expiry: false }
      : { tone: 'pending', label: 'Awaiting approval', attention: true, expiry: false }
  }
  return { tone: 'done', label: `Approved · till ${till}`, attention: false, expiry: false }
}

/** What the agreement card / box says under its chip (Merchant Update hub, Done screen). */
export const agreementLines = (a) => (a === undefined ? ['Loading the agreement…'] : !a ? ['The agreement could not be read just now. Open it to retry.']
  : a.approvalStatus === 'NOT_SUBMITTED' ? ['No agreement yet: upload the signed agreement and enter its dates and credit days.', 'A Proship admin approves it, as with KYC. Booking is not blocked.']
    : [`${fmtDay(a.startDate)} to ${fmtDay(a.expiryDate)}${termsText(a) ? ` · ${termsText(a)}` : ''}.`,
      agreementMissing(a).length ? `Needs ${agreementMissing(a).join(' and ')} before it can be approved.` : 'Signed agreement uploaded.'])

export const listText = (xs, max = 3) => (xs.length > max ? `${xs.slice(0, max).join(', ')} +${xs.length - max} more` : xs.join(', '))

/** KPI cards of one merchant's set-up (Done screen, and steps until their own KPIs are built). */
export const summaryKpis = (m) => {
  const cards = m?.rateCardCouriers || []
  const alloc = m?.allocationCouriers || []
  return [
    { key: 'cards', label: 'Rate cards', value: cards.length ? num(cards.length) : 'None', tone: cards.length ? undefined : 'danger', sub: cards.length ? listText(cards) : 'Orders get no price yet' },
    { key: 'tat', label: 'TAT basis', value: m?.customTatLanes ? 'Custom' : 'Standard', sub: m?.customTatLanes ? `${num(m.customTatLanes)} lanes custom, rest standard` : 'Prozo standard on every lane' },
    { key: 'alloc', label: 'Allocation couriers', value: alloc.length ? num(alloc.length) : 'None', tone: alloc.length ? undefined : 'warn', sub: alloc.length ? listText(alloc) : 'None picked: cheapest carded courier is used' },
    { key: 'zone', label: 'Zone map', value: m?.ownZoneMapPincodes ? 'Own' : 'Standard', sub: m?.ownZoneMapPincodes ? `${num(m.ownZoneMapPincodes)} pincodes override the standard` : 'Prozo standard map' },
  ]
}

/** Status of each set-up area of a live merchant, as chip tone + label (Merchant Update list and hub). */
export const areaStatus = (m) => {
  const cards = (m?.rateCardCouriers || []).length
  const alloc = (m?.allocationCouriers || []).length
  return {
    pricing: { tone: cards ? 'done' : 'blocked', label: cards ? `${cards} rate card${cards > 1 ? 's' : ''}` : 'No rate card' },
    tat: { tone: m?.customTatLanes ? 'pending' : 'none', label: m?.customTatLanes ? `Custom · ${num(m.customTatLanes)} lanes` : 'Standard' },
    allocation: { tone: alloc ? 'done' : 'pending', label: alloc ? `${alloc} courier${alloc > 1 ? 's' : ''}` : 'Cheapest carded' },
    zone: { tone: m?.ownZoneMapPincodes ? 'pending' : 'none', label: m?.ownZoneMapPincodes ? `Own · ${num(m.ownZoneMapPincodes)} pincodes` : 'Standard' },
  }
}
